// src/lib/utils.ts — Shared utilities

/**
 * Generate a unique ID (UUID-like) without external libs.
 */
export function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Format an ISO date string to human-readable "Jan 2025" format.
 */
export function formatDate(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * Format ms duration to "1m 30s" style.
 */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

/**
 * Clamp a number between min and max.
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Combine class names, filtering out falsy values.
 */
export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Capitalize the first letter of a string.
 */
export function capitalize(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Slugify a string for use in URLs.
 */
export function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Check if today is a different day from the given ISO date string.
 */
export function isDifferentDay(isoDate: string): boolean {
  const today = new Date().toISOString().split('T')[0];
  return isoDate.split('T')[0] !== today;
}

/**
 * Get today's date as ISO date string "YYYY-MM-DD".
 */
export function getTodayISO(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Get yesterday's date as ISO date string "YYYY-MM-DD".
 */
export function getYesterdayISO(): string {
  return new Date(Date.now() - 86400000).toISOString().split('T')[0];
}

/**
 * Truncate text to a maximum length with ellipsis.
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Convert "YYYY-MM" to "January 2025" readable format.
 */
export function formatMonthYear(yearMonth: string): string {
  const [year, month] = yearMonth.split('-');
  const date = new Date(Number(year), Number(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Calculate percentage with safety guard against division by zero.
 */
export function percentage(value: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((value / total) * 100);
}

/**
 * Fisher-Yates shuffle — returns a new shuffled array.
 */
export function shuffle<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/**
 * Deep-clone a plain object using JSON serialization.
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj)) as T;
}

/**
 * Format a quiz score as a grade string.
 */
export function getGradeFromScore(percentage: number): {
  emoji: string;
  label: string;
  color: string;
} {
  if (percentage >= 90) return { emoji: '🏆', label: 'Expert', color: '#F59E0B' };
  if (percentage >= 70) return { emoji: '⭐', label: 'Great', color: '#00C9A7' };
  if (percentage >= 50) return { emoji: '✅', label: 'Passed', color: '#3B82F6' };
  return { emoji: '📚', label: 'Keep Practicing', color: '#6B7280' };
}
